import AGENCY_DEFAULT_LOGO from "@/assets/logo-full-dark.png";
import { NoteObj } from "@/types/interfaces/notes";
import {
  ArrowForwardOutlined,
  CampaignOutlined,
  CheckCircleOutlined,
  DescriptionOutlined,
  EventBusyOutlined,
  FolderOutlined,
  ImageOutlined,
  InfoOutlined,
  InsertDriveFileOutlined,
  NotesOutlined,
  PersonOffOutlined,
  ScheduleOutlined,
  StarBorderOutlined,
  TimelineOutlined,
  ChecklistOutlined,
  EmojiEventsOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Alert, Box, Button, Chip, ColorPaletteProp, Grid, Stack, Typography } from "@mui/joy";
import { ReactNode, useState } from "react";
import {
  Agent,
  ProfileFieldGroup,
  ProfileTabValue,
  RISING_STAR_YEAR,
  getKeyDetails,
  getRisingStarLevel,
  getTimeline,
} from "../profileFields";
import { getEmailCampaignHoldReason, getFilteredType, getRelativeTimeString } from "../utils";
import { isNoteOverdue } from "../useAgentNotes";
import FieldGrid from "./FieldGrid";
import FileViewButton from "./FileViewButton";
import SectionCard from "./SectionCard";

type OverviewTabProps = {
  agent: Agent;
  groups: Record<string, ProfileFieldGroup>;
  openNotes: NoteObj[];
  overdueNotes: NoteObj[];
  onOpenTab: (tab: ProfileTabValue) => void;
};

type AgentDocument = { _id?: string; fileName?: string; fileKey?: string };

type AttentionItem = {
  color: ColorPaletteProp;
  icon: ReactNode;
  title: string;
  description?: string;
  action?: { label: string; tab: ProfileTabValue };
};

const getYearsSince = (value?: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  const years = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return years >= 0 ? years : null;
};

const formatTenure = (years: number | null) => {
  if (years === null) return "—";
  if (years < 1) return "< 1 yr";
  return `${Math.floor(years)} yr${Math.floor(years) === 1 ? "" : "s"}`;
};

const getAttentionItems = (agent: Agent, overdueNotes: NoteObj[]): AttentionItem[] => {
  const items: AttentionItem[] = [];

  if (agent?.status === "RESIGNED") {
    items.push({
      color: "danger",
      icon: <PersonOffOutlined />,
      title: "Agent has resigned",
      description: agent.dates?.terminationDate ? `Termination date: ${agent.dates.terminationDate}` : undefined,
      action: { label: "Key dates", tab: "business" },
    });
  } else if (agent?.status === "LEAVING") {
    items.push({
      color: "warning",
      icon: <EventBusyOutlined />,
      title: "Agent is leaving",
      description: agent.dates?.terminationDate
        ? `Termination date: ${agent.dates.terminationDate}`
        : "No termination date recorded yet.",
      action: { label: "Key dates", tab: "business" },
    });
  }

  if (overdueNotes.length) {
    items.push({
      color: "warning",
      icon: <ScheduleOutlined />,
      title: `${overdueNotes.length} note${overdueNotes.length === 1 ? " is" : "s are"} past the review date`,
      action: { label: "Review notes", tab: "notes" },
    });
  }

  const campaignHoldReason = agent ? getEmailCampaignHoldReason(agent) : null;
  if (campaignHoldReason) {
    items.push({
      color: "neutral",
      icon: <CampaignOutlined />,
      title: "Email campaigns are on hold",
      description: campaignHoldReason.replace(/^Email campaign suspended because /, "Because "),
      action: { label: "Marketing", tab: "marketing" },
    });
  }

  return items;
};

function StatTile({ label, value, icon }: { label: string; value: ReactNode; icon: ReactNode }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={1.5}
      sx={{
        p: 2,
        borderRadius: "lg",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.surface",
        boxShadow: "xs",
        height: "100%",
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "md",
          backgroundColor: "neutral.softBg",
          color: "text.secondary",
          "--Icon-color": "currentColor",
        }}
      >
        {icon}
      </Stack>
      <Box sx={{ minWidth: 0 }}>
        <Typography level="body-xs" textColor="text.tertiary">
          {label}
        </Typography>
        <Typography level="title-lg" noWrap>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

function AboutCard({ description }: { description?: string }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = (description?.length || 0) > 480;

  return (
    <SectionCard title="About" icon={<NotesOutlined />}>
      {description ? (
        <Box>
          <Typography
            level="body-md"
            sx={{
              whiteSpace: "pre-line",
              lineHeight: 1.7,
              ...(isLong &&
                !expanded && {
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
            }}
          >
            {description}
          </Typography>
          {isLong && (
            <Button size="sm" variant="plain" onClick={() => setExpanded((prev) => !prev)} sx={{ mt: 0.5, ml: -1 }}>
              {expanded ? "Show less" : "Read more"}
            </Button>
          )}
        </Box>
      ) : (
        <Typography level="body-sm" textColor="text.tertiary">
          No description added yet.
        </Typography>
      )}
    </SectionCard>
  );
}

function TimelineCard({ groups, onOpenTab }: Pick<OverviewTabProps, "groups" | "onOpenTab">) {
  const milestones = getTimeline(groups);

  return (
    <SectionCard
      title="Milestones"
      icon={<TimelineOutlined />}
      action={
        <Button size="sm" variant="plain" onClick={() => onOpenTab("business")}>
          All dates
        </Button>
      }
    >
      {milestones.length ? (
        <Stack component="ol" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {milestones.map((milestone, index) => {
            const isTermination = milestone.label === "Termination Date";
            const isLast = index === milestones.length - 1;

            return (
              <Stack component="li" key={milestone.label} direction="row" gap={1.5}>
                <Stack alignItems="center" sx={{ width: 12, flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      mt: "6px",
                      borderRadius: "50%",
                      backgroundColor: isTermination ? "danger.500" : "primary.500",
                      boxShadow: "0 0 0 3px var(--joy-palette-background-surface)",
                    }}
                  />
                  {!isLast && <Box sx={{ flex: 1, width: "2px", backgroundColor: "divider", my: 0.5 }} />}
                </Stack>
                <Box sx={{ pb: isLast ? 0 : 2 }}>
                  <Typography level="body-xs" textColor="text.tertiary">
                    {milestone.label}
                  </Typography>
                  <Typography level="title-sm" textColor={isTermination ? "danger.600" : "text.primary"}>
                    {milestone.value}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography level="body-sm" textColor="text.tertiary">
          No dates recorded yet.
        </Typography>
      )}
    </SectionCard>
  );
}

function RecentNotesCard({ openNotes, onOpenTab }: Pick<OverviewTabProps, "openNotes" | "onOpenTab">) {
  const recent = [...openNotes]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 3);

  return (
    <SectionCard
      title="Open Notes"
      icon={<ChecklistOutlined />}
      action={
        openNotes.length > 0 && (
          <Chip size="sm" variant="soft" color="warning">
            {openNotes.length}
          </Chip>
        )
      }
    >
      {recent.length ? (
        <Stack gap={1}>
          {recent.map((note) => (
            <Box
              key={note._id}
              sx={{ p: 1.5, borderRadius: "md", backgroundColor: "background.level1", cursor: "pointer" }}
              onClick={() => onOpenTab("notes")}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography level="title-sm" noWrap>
                  {getFilteredType(note.type)}
                </Typography>
                {isNoteOverdue(note) && (
                  <Chip size="sm" variant="soft" color="warning">
                    Overdue
                  </Chip>
                )}
              </Stack>
              <Typography
                level="body-sm"
                textColor="text.secondary"
                sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
              >
                {note.comment}
              </Typography>
              <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                {getRelativeTimeString(note.createdAt)}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" gap={1}>
          <CheckCircleOutlined sx={{ color: "success.500" }} />
          <Typography level="body-sm" textColor="text.secondary">
            No open notes
          </Typography>
        </Stack>
      )}
      <Button
        size="sm"
        variant="outlined"
        color="neutral"
        endDecorator={<ArrowForwardOutlined />}
        onClick={() => onOpenTab("notes")}
      >
        {openNotes.length ? "Go to notes" : "Add or view notes"}
      </Button>
    </SectionCard>
  );
}

function DocumentsCard({ documents }: { documents: AgentDocument[] }) {
  return (
    <SectionCard
      title="Documents"
      icon={<FolderOutlined />}
      action={
        documents.length > 0 && (
          <Chip size="sm" variant="soft">
            {documents.length}
          </Chip>
        )
      }
    >
      {documents.length ? (
        <Stack gap={1}>
          {documents.map((doc, idx) => (
            <Stack
              key={doc._id || idx}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              sx={{ p: 1, pl: 1.5, borderRadius: "md", backgroundColor: "background.level1" }}
            >
              <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                <InsertDriveFileOutlined sx={{ color: "text.tertiary" }} fontSize="md" />
                <Typography level="body-sm" textColor="text.primary" noWrap title={doc.fileName}>
                  {doc.fileName || "Untitled document"}
                </Typography>
              </Stack>
              <Box sx={{ flexShrink: 0 }}>
                <FileViewButton file={doc.fileKey} buttonText="View" />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : (
        <Stack direction="row" alignItems="center" gap={1} sx={{ color: "text.tertiary" }}>
          <DescriptionOutlined />
          <Typography level="body-sm" textColor="text.tertiary">
            No documents uploaded
          </Typography>
        </Stack>
      )}
    </SectionCard>
  );
}

function OverviewTab({ agent, groups, openNotes, overdueNotes, onOpenTab }: OverviewTabProps) {
  const documents: AgentDocument[] = Array.isArray((agent as any)?.documents) ? (agent as any).documents : [];
  const attentionItems = getAttentionItems(agent, overdueNotes);
  const risingStarLevel = getRisingStarLevel(agent);
  const campaignsOnHold = agent ? !!getEmailCampaignHoldReason(agent) : false;

  return (
    <Stack gap={2.5}>
      {/* Needs attention */}
      {attentionItems.length > 0 && (
        <Stack gap={1}>
          {attentionItems.map((item) => (
            <Alert
              key={item.title}
              variant="soft"
              color={item.color}
              startDecorator={item.icon}
              endDecorator={
                item.action && (
                  <Button
                    size="sm"
                    variant="plain"
                    color={item.color}
                    endDecorator={<ArrowForwardOutlined />}
                    onClick={() => onOpenTab(item.action!.tab)}
                  >
                    {item.action.label}
                  </Button>
                )
              }
              sx={{ alignItems: "center", borderRadius: "lg" }}
            >
              <Box>
                <Typography level="title-sm" textColor="inherit">
                  {item.title}
                </Typography>
                {item.description && (
                  <Typography level="body-sm" textColor="inherit" sx={{ opacity: 0.85 }}>
                    {item.description}
                  </Typography>
                )}
              </Box>
            </Alert>
          ))}
        </Stack>
      )}

      {/* At a glance */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 2 }}>
        <StatTile
          label="With TTAND"
          value={formatTenure(getYearsSince(agent?.originalJoinDate))}
          icon={<StarBorderOutlined />}
        />
        <StatTile
          label="In travel industry"
          value={formatTenure(getYearsSince(agent?.dateStarted))}
          icon={<TimelineOutlined />}
        />
        <StatTile
          label="Open notes"
          value={
            <Stack direction="row" alignItems="center" gap={0.75} component="span">
              {openNotes.length}
              {overdueNotes.length > 0 && <WarningAmberOutlined sx={{ color: "warning.500" }} fontSize="md" />}
            </Stack>
          }
          icon={<ChecklistOutlined />}
        />
        <StatTile
          label="Email campaigns"
          value={
            <Typography component="span" textColor={campaignsOnHold ? "neutral.600" : "success.600"}>
              {campaignsOnHold ? "On hold" : "Active"}
            </Typography>
          }
          icon={<CampaignOutlined />}
        />
        <StatTile
          label={`Rising Star ${RISING_STAR_YEAR}`}
          value={risingStarLevel ?? "—"}
          icon={<EmojiEventsOutlined />}
        />
      </Box>

      <Grid container spacing={2.5} columns={12}>
        <Grid xs={12} lg={8}>
          <Stack gap={2.5}>
            <AboutCard description={agent?.description} />
            <SectionCard title="Key Details" icon={<InfoOutlined />}>
              <FieldGrid fields={getKeyDetails(groups)} />
            </SectionCard>
            <TimelineCard groups={groups} onOpenTab={onOpenTab} />
          </Stack>
        </Grid>

        <Grid xs={12} lg={4}>
          <Stack gap={2.5}>
            <RecentNotesCard openNotes={openNotes} onOpenTab={onOpenTab} />
            <DocumentsCard documents={documents} />
            <SectionCard title="Branding" icon={<ImageOutlined />}>
              <Box
                sx={{
                  borderRadius: "md",
                  backgroundColor: "background.level1",
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                  p: 2,
                }}
              >
                <img
                  src={agent?.agencyLogo || AGENCY_DEFAULT_LOGO}
                  alt="Agency Logo"
                  style={{ maxWidth: "100%", maxHeight: "100%", display: "block", objectFit: "contain" }}
                />
              </Box>
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default OverviewTab;
